import React, { useState, useEffect } from "react";
import patrimonioService from "../../services/patrimonioService";
import { getLocalizacaoLabel } from "./patrimonioConstants";

/**
 * Modal de movimentação de patrimônio (transferência de local/obra/responsável).
 * Usado na listagem e na edição.
 *
 * Props:
 * - isVisible: boolean
 * - patrimonio: bem a movimentar (com local/obra/responsavel atuais)
 * - locais: lista de LocalPatrimonio
 * - obras: lista de Obras
 * - onClose: () => void
 * - onSuccess: (resultado) => void — chamado após movimentar com sucesso
 */
const MovimentarPatrimonioModal = ({ isVisible, patrimonio, locais, obras, onClose, onSuccess }) => {
    const [destinoTipo, setDestinoTipo] = useState("manter"); // manter | local | obra
    const [paraLocalId, setParaLocalId] = useState("");
    const [paraObraId, setParaObraId] = useState("");
    const [paraResponsavel, setParaResponsavel] = useState("");
    const [dataMovimentacao, setDataMovimentacao] = useState("");
    const [observacao, setObservacao] = useState("");
    const [erro, setErro] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isVisible && patrimonio) {
            setDestinoTipo("manter");
            setParaLocalId("");
            setParaObraId("");
            setParaResponsavel(patrimonio.responsavel || "");
            setDataMovimentacao(new Date().toISOString().split("T")[0]);
            setObservacao("");
            setErro("");
        }
    }, [isVisible, patrimonio]);

    if (!isVisible || !patrimonio) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErro("");

        if (destinoTipo === "local" && !paraLocalId) {
            setErro("Selecione o local de destino.");
            return;
        }
        if (destinoTipo === "obra" && !paraObraId) {
            setErro("Selecione a obra de destino.");
            return;
        }

        const dados = {
            paraResponsavel,
            dataMovimentacao,
            observacao,
        };
        if (destinoTipo === "local") dados.paraLocalId = paraLocalId;
        if (destinoTipo === "obra") dados.paraObraId = paraObraId;

        setIsSaving(true);
        try {
            const resultado = await patrimonioService.movimentar(patrimonio.id, dados);
            onSuccess(resultado);
        } catch (error) {
            console.error("Erro ao movimentar patrimônio:", error);
            setErro(error.response?.data?.error || "Erro ao movimentar patrimônio.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-white rounded-md shadow-md border border-gray-200 p-6 flex flex-col gap-4">
                <div className="text-stone-800 text-lg font-semibold">
                    Movimentar Patrimônio
                </div>

                {/* Situação atual */}
                <div className="bg-stone-50 border border-slate-200 rounded-md p-3 text-sm flex flex-col gap-1">
                    <div>
                        <span className="text-slate-500">Bem: </span>
                        <span className="text-slate-700 font-medium">{patrimonio.codigo} - {patrimonio.nome}</span>
                    </div>
                    <div>
                        <span className="text-slate-500">Localização atual: </span>
                        <span className="text-slate-700 font-medium">{getLocalizacaoLabel(patrimonio)}</span>
                    </div>
                    <div>
                        <span className="text-slate-500">Responsável atual: </span>
                        <span className="text-slate-700 font-medium">{patrimonio.responsavel || "-"}</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {/* Destino */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Nova localização</label>
                        <div className="flex flex-wrap gap-4 text-sm">
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="destinoTipo"
                                    checked={destinoTipo === "manter"}
                                    onChange={() => setDestinoTipo("manter")}
                                />
                                <span>Manter atual</span>
                            </label>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="destinoTipo"
                                    checked={destinoTipo === "local"}
                                    onChange={() => setDestinoTipo("local")}
                                />
                                <span>Local</span>
                            </label>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="destinoTipo"
                                    checked={destinoTipo === "obra"}
                                    onChange={() => setDestinoTipo("obra")}
                                />
                                <span>Obra</span>
                            </label>
                        </div>
                    </div>

                    {destinoTipo === "local" && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Local de destino</label>
                            <select
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                value={paraLocalId}
                                onChange={(e) => setParaLocalId(e.target.value)}
                            >
                                <option value="">Selecionar</option>
                                {locais
                                    .filter((l) => l.ativo !== false)
                                    .map((l) => (
                                        <option key={l.id} value={l.id}>{l.nome}</option>
                                    ))}
                            </select>
                        </div>
                    )}

                    {destinoTipo === "obra" && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Obra de destino</label>
                            <select
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                value={paraObraId}
                                onChange={(e) => setParaObraId(e.target.value)}
                            >
                                <option value="">Selecionar</option>
                                {obras.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.codigoProjeto ? `${o.codigoProjeto} - ` : ""}{o.nome || o.nomeProjeto}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Responsável */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Responsável</label>
                        <input
                            type="text"
                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            placeholder="Nome da pessoa responsável pelo bem"
                            value={paraResponsavel}
                            onChange={(e) => setParaResponsavel(e.target.value)}
                        />
                    </div>

                    {/* Data */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Data da movimentação</label>
                        <input
                            type="date"
                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            value={dataMovimentacao}
                            onChange={(e) => setDataMovimentacao(e.target.value)}
                        />
                    </div>

                    {/* Observação */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm text-slate-700 font-medium">Observação</label>
                        <textarea
                            rows={2}
                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            placeholder="Ex: enviado para uso na obra, devolvido ao escritório..."
                            value={observacao}
                            onChange={(e) => setObservacao(e.target.value)}
                        />
                    </div>

                    {erro && (
                        <div className="text-red-600 text-sm">{erro}</div>
                    )}

                    <div className="flex w-full justify-between gap-4 mt-2">
                        <button
                            type="button"
                            className="w-full px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="w-full px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md disabled:opacity-50"
                            disabled={isSaving}
                        >
                            {isSaving ? "Movimentando..." : "Movimentar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MovimentarPatrimonioModal;
